/**
 * AdminDraftCleanup – admin-only UI to list and soft-delete empty product drafts.
 *
 * Uses RPCs:
 * - admin_list_empty_product_drafts(p_producer_id?, p_older_than_days)
 * - admin_soft_delete_product_drafts(p_ids uuid[])
 */

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface EmptyDraft {
  id: string;
  producer_id: string;
  title: string | null;
  price_ore: number;
  status: string;
  created_at: string;
  media_count: number;
}

export default function AdminDraftCleanup() {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { data: drafts, isLoading, error } = useQuery({
    queryKey: ['admin-empty-product-drafts'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('admin_list_empty_product_drafts', {
        p_older_than_days: 2,
      });
      if (error) throw error;
      return (data as unknown as EmptyDraft[]) ?? [];
    },
  });

  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (!drafts) return;
    if (selected.size === drafts.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(drafts.map(d => d.id)));
    }
  };

  const handleDelete = async () => {
    if (selected.size === 0) return;
    setIsDeleting(true);

    try {
      const { data, error } = await supabase.rpc('admin_soft_delete_product_drafts', {
        p_ids: Array.from(selected),
      });

      if (error) throw error;

      const result = data as unknown as { ok: boolean; deleted_count: number };
      toast.success(`${result.deleted_count} utkast raderade`);
      setSelected(new Set());
      queryClient.invalidateQueries({ queryKey: ['admin-empty-product-drafts'] });
    } catch (err: any) {
      toast.error('Kunde inte radera', { description: err?.message });
    } finally {
      setIsDeleting(false);
      setConfirmOpen(false);
    }
  };

  return (
    <AdminLayout>
      <div className="w-full max-w-3xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Städa tomma produktutkast</h1>
          <p className="text-muted-foreground mt-1">
            Visar autoskapade utkast utan titel, beskrivning, pris eller bilder (äldre än 2 dagar).
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Kandidater för borttagning</CardTitle>
            <CardDescription>
              Markera de utkast du vill ta bort (soft-delete). Åtgärden loggas i admin-audit-log.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading && (
              <div className="flex items-center gap-2 py-8 justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Söker...</span>
              </div>
            )}

            {error && (
              <div className="flex items-center gap-2 text-destructive py-4">
                <AlertTriangle className="h-4 w-4" />
                <span className="text-sm">Kunde inte hämta data: {(error as Error).message}</span>
              </div>
            )}

            {!isLoading && !error && drafts && drafts.length === 0 && (
              <p className="text-sm text-muted-foreground py-8 text-center">
                Inga tomma utkast hittades. 🎉
              </p>
            )}

            {!isLoading && drafts && drafts.length > 0 && (
              <div className="space-y-3">
                {/* Select all */}
                <div className="flex items-center gap-2 pb-2 border-b">
                  <Checkbox
                    checked={selected.size === drafts.length}
                    onCheckedChange={toggleAll}
                  />
                  <span className="text-sm font-medium">
                    Markera alla ({drafts.length} st)
                  </span>
                </div>

                {/* List */}
                {drafts.map((draft) => (
                  <div key={draft.id} className="flex items-center gap-3 py-2">
                    <Checkbox
                      checked={selected.has(draft.id)}
                      onCheckedChange={() => toggleSelect(draft.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-mono text-muted-foreground truncate">
                        {draft.id}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Producent: {draft.producer_id.slice(0, 8)}… · 
                        Skapad: {new Date(draft.created_at).toLocaleDateString('sv-SE')} · 
                        Titel: {draft.title || '(tom)'} · 
                        Media: {draft.media_count}
                      </p>
                    </div>
                  </div>
                ))}

                {/* Actions */}
                <div className="flex items-center justify-between pt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    {selected.size} av {drafts.length} markerade
                  </p>
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={selected.size === 0 || isDeleting}
                    onClick={() => setConfirmOpen(true)}
                  >
                    {isDeleting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                    <Trash2 className="h-4 w-4 mr-2" />
                    Radera markerade
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Confirm dialog */}
        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Bekräfta borttagning</AlertDialogTitle>
              <AlertDialogDescription>
                Du är på väg att soft-delete {selected.size} tomma produktutkast. 
                Åtgärden loggas men kan inte enkelt ångras.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Avbryt</AlertDialogCancel>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Radera {selected.size} utkast
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}
